import { createContext, useContext, type ReactNode } from "react";
import type {
  CurrentPageContext,
  PlatformNavigationAction,
  PrivatePlayerAIHandoff,
  WorkspaceAccessResourceType,
} from "@playbookai/types";

/**
 * Optional callbacks the host shell injects so a feature workspace can drive
 * shell-owned chrome it must not import directly (keeps feature packages
 * decoupled from `apps/shell`, same rationale as {@link ApiClientProvider}).
 * Unlike the API client, this context defaults to no-ops: a package renders
 * fine with no provider (unit tests, the component catalog), and callers always
 * guard with `?.`.
 *
 * Graduated here from `solve-feature` (PB-220) so a second module
 * (`hoshin-feature`) consumes the same context object rather than importing a
 * sibling feature. `solve-feature/src/api/shell-bridge` re-exports these.
 */
/**
 * The right-rail slot is single-tenant: the host shell's Player AI rail XOR a
 * module rail such as SOLVE Audit or People/access. The shell owns one
 * `rightRailTenant` value (see
 * {@link ShellBridge.rightRailTenant}); opening one tenant evicts the other.
 */
export type RightRailTenant =
  | "audit"
  | "access"
  | "compare"
  | "kpi"
  // VSM preparation: readiness facts and the shared discussion, in one rail.
  // A single tenant rather than two because the coupling is real — the
  // readiness panel's open-discussion count is derived from exactly the threads
  // beneath it, and splitting them would let a reader see "2 open" in one panel
  // and one thread in the other with nothing saying which is stale.
  | "vsm-preparation"
  | "playerAI";

export type ModuleRailKind =
  | "access"
  | "audit"
  | "compare"
  | "kpi"
  | "vsm-preparation";

export type PlayerAIWorkspacePresence =
  | Readonly<{
      workspaceType: "solve" | "hoshin";
      workspaceId: string;
      status: "unknown";
    }>
  | Readonly<{
      workspaceType: "solve" | "hoshin";
      workspaceId: string;
      status: "known";
      presentUserCount: number;
    }>;

/** Module-owned authorized member reads; the shell never imports feature clients. */
export interface PlayerAIMentionRoster {
  readonly ownerId?: string | undefined;
  readonly pageMembers: (
    cursor: string | null,
    signal: AbortSignal,
  ) => Promise<{
    readonly items: readonly { readonly userId: string }[];
    readonly nextCursor: string | null;
  }>;
  readonly resolveIdentities: (
    userIds: readonly string[],
    signal: AbortSignal,
  ) => Promise<
    readonly { readonly userId: string; readonly displayName: string | null }[]
  >;
}

/**
 * PB-4923 AC6 — the lookups a Player AI notice needs to name things.
 *
 * The rail is shell chrome mounted in `AppLayout`, OUTSIDE the module page, so
 * it cannot reach `WidgetActorNameProvider` or the module's query cache through
 * React context. A skip notice would otherwise be stuck saying "Someone else"
 * and naming a field by its key, when the workspace directly beside it has both
 * answers already in memory.
 *
 * Both resolvers are SYNCHRONOUS and read only what the module has already
 * loaded for this workspace — presence and the chunk-resolved roster for the
 * name, the widget query cache for the params. No request, no directory read,
 * no new permission: if the module could not already show it, this returns
 * null and the copy falls back.
 *
 * Scoped by `workspaceType`/`workspaceId` and matched by the shell the same way
 * presence is, so a stale registration from another workspace can never answer.
 */
export type PlayerAIWidgetContext = Readonly<{
  /**
   * The full workspace set, not presence's `solve | hoshin`. VSM and the Hoshin
   * workshop host CONTEXTUAL-PRIVATE Player AI threads rather than workspace
   * conversations, and a notice on those surfaces needs naming just as much —
   * matching is on the workspace, not on the conversation kind.
   */
  workspaceType: "solve" | "hoshin" | "hoshin_workshop" | "vsm";
  workspaceId: string;
  /** A display name for an actor id, or `null` when it cannot be resolved. */
  nameForActor: (userId: string) => string | null;
  /** The widget's current params from the module's cache, or `undefined`. */
  widgetParams: (widgetId: string) => unknown;
  /**
   * Task 9 — bring one widget into view on THIS workspace's canvas and outline
   * it, answering with what actually happened.
   *
   * The module owns it because the module owns the canvas: only it knows the
   * widget's current position, whether the reader's phase set shows it, and how
   * to move the viewport. The rail is shell chrome mounted outside the page and
   * has none of that.
   *
   * Synchronous and total. A "did it work" the row can render immediately is
   * what keeps a deleted or unreachable destination from looking like a dead
   * button, and the answer is already in the canvas document the module holds.
   *
   * Absent on a workspace with no canvas, in which case the rail offers no
   * navigation at all rather than one that cannot land.
   */
  revealWidget?: (widgetId: string) => PlayerAIWidgetRevealOutcome;
  /** Complete authorized roster for mentions, loaded on first use and filtered locally. */
  peopleSearchResource?: Readonly<{
    resourceType: WorkspaceAccessResourceType;
    resourceId: string;
    roster: PlayerAIMentionRoster;
  }>;
}>;

/** What {@link PlayerAIWidgetContext.revealWidget} did. */
export type PlayerAIWidgetRevealOutcome =
  | { readonly status: "revealed" }
  | { readonly status: "missing" }
  | { readonly status: "hidden"; readonly reason?: string }
  | { readonly status: "unavailable" };

/** Module-owned override for the shell's route-derived Player AI scope. */
export type PlayerAIScopeOverride =
  | Readonly<{ conversationType: "private" }>
  | Readonly<{
      conversationType: "private";
      // The workspace types with contextual private threads: a Hoshin plan
      // (Edit/Monitor advice) and a Hoshin workshop (Ideate).
      workspaceType: "hoshin" | "hoshin_workshop";
      workspaceId: string;
    }>
  | Readonly<{
      conversationType: "workspace";
      workspaceType: "solve" | "hoshin" | "hoshin_workshop";
      workspaceId: string;
    }>;

export interface PlayerAIClientAction {
  /**
   * Module-native client action only. Cross-app platform mutations use the
   * closed PlatformAction contract and are applied by owner services.
   */
  readonly action_id: string;
  readonly contract_version: number;
  readonly label: string;
  readonly [key: string]: unknown;
}

export interface PlayerAIClientSurfaceRegistration {
  /**
   * Which conversation the surface serves. Private surfaces handle a member's
   * own thread (e.g. the Ideate private canvas); the workshop session page
   * registers a `workspace` surface for shared-conversation client actions
   * (e.g. the facilitator's duplicate-review drawer).
   */
  readonly conversationType: "private" | "workspace";
  readonly workspaceType: "solve" | "hoshin" | "hoshin_workshop";
  readonly workspaceId: string;
  readonly interactionContext: Readonly<Record<string, unknown>>;
  /** Capture ephemeral document state at message send, never at registration. */
  readonly captureInteractionContext?: () => Readonly<Record<string, unknown>>;
  readonly applyAction: (action: PlayerAIClientAction) => boolean;
}

/**
 * Identity of one published client surface, so a late unmount releases only its
 * OWN registration rather than whatever now holds the single slot.
 */
export interface PlayerAIClientSurfaceToken {
  readonly registrationId: number;
}

export interface ModuleRailClaimInput {
  readonly kind: ModuleRailKind;
  readonly ownerKey: string;
  readonly resourceKey: string;
}

export interface ModuleRailClaimToken extends ModuleRailClaimInput {
  readonly claimId: string;
  readonly generation: number;
}

export interface ShellBridge {
  /** Publish a screen's method phase for Knowledge Studio; returns an owner-safe cleanup. */
  readonly publishKnowledgeContext?: (context: {
    readonly routeKey: string;
    readonly module: string;
    readonly phase?: string;
  }) => () => void;
  /**
   * Rename whichever shell tab is ACTIVE when called. A workspace must not use
   * this for its own title: see {@link setTabLabelForPath}.
   */
  readonly setActiveTabLabel?: (label: string) => void;
  /**
   * Rename the shell tab that OWNS `path` — the tab the shell activates for
   * that URL — e.g. to the loaded entity's title. A workspace passes the path
   * it is rendering, so its title lands on its own tab.
   *
   * "The active tab" is the wrong target for that. Two entity tabs of one
   * module share a route, so switching between them re-renders the same
   * mounted workspace with new params; the router applies that in a
   * transition, LATER than the shell's tab state, and the workspace's effects
   * run before the shell's URL→tab sync in the same commit. A title from that
   * render, sent to the active tab, landed on the previous tab: two open VSMs
   * both ended up named after one.
   *
   * A module home that holds `path` only by prefix, while the module declares
   * a more specific entity route, is not its owner: the title belongs to that
   * entity's tab, and waits for the shell to create it. Otherwise a no-op when
   * no tab owns `path`.
   */
  readonly setTabLabelForPath?: (args: { path: string; label: string }) => void;
  /**
   * Open (or reactivate) a dedicated shell tab for a SOLVE, keyed by its id and
   * labelled with its title, then navigate to its workspace — the prototype's
   * "each SOLVE is its own tab" behaviour. The feature calls this instead of
   * navigating itself so the shell owns tab identity.
   */
  readonly openSolve?: (args: {
    solveId: string;
    title: string;
    /** Carried for this navigation only; shell-tab reactivation remains pathname-only. */
    search?: string;
  }) => void;
  /** Close a SOLVE's tab (e.g. after deleting it from its workspace). */
  readonly closeSolveTab?: (solveId: string) => void;
  /**
   * Open (or reactivate) a titled shell tab for any module entity, then
   * navigate to it: route `${routeBase}/${instanceId}/${entityId}` when an
   * instance id is supplied, otherwise `${routeBase}/${entityId}` for a
   * single-segment module. A host may mint the instance segment when the
   * module declares a nested entity route and the caller has none (for example,
   * opening a VSM from Hoshin); tab id `${moduleId}-${entityId}`, label
   * `title`. The module-agnostic
   * generalization of {@link openSolve} supports both nested SOLVE-style routes
   * and single-segment routes such as Hoshin. The shell owns tab identity, so a
   * feature calls this rather than navigating itself.
   */
  readonly openModuleEntityTab?: (args: {
    /** Optional query string for the requested workspace view. */
    search?: string;
    moduleId: string;
    instanceId?: string;
    entityId: string;
    title: string;
  }) => void;
  /**
   * Close a module entity's tab (the counterpart to {@link openModuleEntityTab},
   * keyed the same `${moduleId}-${entityId}`), then let the URL follow the newly
   * active tab. Used after deleting a module entity, or by single-segment
   * modules whose entity tab must close rather than navigate to a parent route.
   */
  readonly closeModuleEntityTab?: (args: {
    moduleId: string;
    entityId: string;
    /** Confirmed terminal resource loss; force-close even when the tab is pinned. */
    terminalClose?: true;
    /** Previously captured exact token to release; terminal closure never mints one. */
    terminalClaim?: ModuleRailClaimToken;
  }) => void;
  /**
   * Which tenant currently occupies the host's single right-rail slot, or
   * `null` when the right edge is empty. The shell owns this value; a module
   * reads it to gate its own rail render (e.g. SOLVE shows its Audit rail only
   * when this is `"audit"` and People/access only when this is `"access"`).
   * Treat `undefined` (no provider — catalog/tests) as `null`.
   */
  readonly rightRailTenant?: RightRailTenant | null;
  /**
   * Open a right-rail tenant, evicting whichever was open (single-tenant XOR).
   * A module calls `openRail("audit")` / `openRail("access")` instead of holding
   * its own open-state.
   */
  readonly openRail?: (tenant: RightRailTenant) => void;
  /**
   * Claim the module rail and capture the exact token required to release it.
   * A newer claim evicts the previous occupant.
   */
  readonly claimModuleRail?: (
    input: ModuleRailClaimInput,
  ) => ModuleRailClaimToken;
  /** Release the rail only when `token` is the exact active claim. */
  readonly releaseModuleRail?: (token: ModuleRailClaimToken) => void;
  /**
   * Close a legacy right-rail occupant. A scoped close only closes the matching
   * legacy/PlayerAI occupant; the no-argument form closes any legacy/PlayerAI
   * occupant. Neither form can close a token-owned module claim — exact tokens
   * govern claim release.
   */
  readonly closeRail?: (tenant?: RightRailTenant) => void;
  /**
   * Publish ephemeral live-presence state for the active Player AI workspace.
   * This is a UI hint only; it never crosses the authorization boundary.
   */
  /**
   * PB-4923 AC6 — publish the name/params lookups a Player AI notice needs.
   * `undefined` clears them, which a module does on unmount so a departed
   * workspace cannot keep answering.
   */
  readonly publishPlayerAIWidgetContext?: (
    context: PlayerAIWidgetContext | undefined,
  ) => void;
  readonly publishPlayerAIWorkspacePresence?: (
    presence: PlayerAIWorkspacePresence,
  ) => void;
  /**
   * Ask the mounted Player AI surface to reconcile its active conversation
   * after a product-owned resource change. Closed surfaces hydrate normally
   * when opened, so the host only needs to retain a monotonic refresh nudge.
   */
  readonly requestPlayerAIConversationRefresh?: () => void;
  /**
   * Override route-derived Player AI scope while a module owns a more precise
   * sub-resource. `null` intentionally suppresses Player AI; `undefined`
   * releases the override on unmount.
   */
  readonly publishPlayerAIScopeOverride?: (
    scope: PlayerAIScopeOverride | null | undefined,
  ) => void;
  /** Publish bounded, ephemeral route context. Context never grants access. */
  readonly publishPlayerAICurrentPageContext?: (
    context: CurrentPageContext | undefined,
  ) => void;
  /** Resolve and open an entity reference through the shell route registry. */
  readonly openPlayerAIEntity?: (action: PlatformNavigationAction) => void;
  /** Open private Player AI with an entity chip and unsent composer text. */
  readonly continueInPrivatePlayerAI?: (
    handoff: PrivatePlayerAIHandoff,
  ) => void;
  /**
   * Publish bounded module context and a client-action handler for private AI.
   * Returns the registration's token; pass it to `releasePlayerAIClientSurface`
   * rather than clearing unconditionally.
   */
  readonly publishPlayerAIClientSurface?: (
    surface: PlayerAIClientSurfaceRegistration | undefined,
  ) => PlayerAIClientSurfaceToken;
  /** Clear the surface only when `token` is still the active registration. */
  readonly releasePlayerAIClientSurface?: (
    token: PlayerAIClientSurfaceToken,
  ) => void;
}

const ShellBridgeContext = createContext<ShellBridge>({});

export interface ShellBridgeProviderProps {
  readonly value: ShellBridge;
  readonly children: ReactNode;
}

export function ShellBridgeProvider({
  value,
  children,
}: ShellBridgeProviderProps): JSX.Element {
  return (
    <ShellBridgeContext.Provider value={value}>
      {children}
    </ShellBridgeContext.Provider>
  );
}

export function useShellBridge(): ShellBridge {
  return useContext(ShellBridgeContext);
}
